import type { ButtonProps } from '@openx/components-core/lib/Button/Button.tsx';
import type { UseConfirmationDialogParams } from '@openx/components-core/lib/Dialog/ConfirmationDialog/types.ts';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog.tsx';
import {
  type LalSegmentBase,
  type LalSegmentRowActionType,
  lalSegmentRowActionType,
  type SegmentRowActionType,
} from '@openx/types/segments.ts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getActionLabels } from '../../constants.ts';
import { useSegmentContext } from '../utils/context/SegmentContext.tsx';

interface ExtraButtonOptions {
  extraBtnLabel: string;
  extraButtonColor: ButtonProps['color'];
  onExtraAction: () => void;
  showExtraBtn: boolean;
}

interface OpenActionModalParams {
  action: SegmentRowActionType;
  confirmationButtonColor?: ButtonProps['color'];
  confirmationDialog: {
    open: (params: Partial<UseConfirmationDialogParams>) => void;
    close: () => void;
    setLoading: (loading: boolean) => void;
  };
  confirmationLabel: string;
  content: UseConfirmationDialogParams['content'];
  extraButtonOptions?: ExtraButtonOptions;
  isBulkMode?: boolean;
  onConfirmed(segments: LalSegmentBase[]): Promise<void>;
  segments: LalSegmentBase[];
  title: string;
}

const NO_OP = (): void => {
  // intentional no-op (kept to satisfy interface contract)
};

const openActionModal = ({
  confirmationDialog,
  confirmationLabel,
  content,
  extraButtonOptions,
  segments,
  title,
  onConfirmed,
  confirmationButtonColor = 'error',
}: OpenActionModalParams) => {
  const { open, close } = confirmationDialog;

  open({
    confirmationButtonColor,
    confirmationLabel,
    content,
    customContent: true,
    hideConfirm: segments.length === 0,
    onConfirmed: async () => {
      try {
        await onConfirmed(segments);
      } finally {
        close();
      }
    },
    showExtraBtn: false,
    title: title,
    ...(extraButtonOptions ?? {}),
  });
};

type CallLalSegmentsAction<T extends LalSegmentBase> = {
  action: LalSegmentRowActionType;
  content: React.JSX.Element | string;
  confirmationDisabled?: boolean;
  isBulkMode?: boolean;
  onExtraAction?: () => void;
  onFinish?: () => void;
  preProcessSegments?: (segments: T[]) => T[];
  segments: T[];
  skipModal?: boolean;
};

export const useLalSegmentsOperations = () => {
  const { t } = useTranslation();
  const { useLalSegmentsOperation } = useSegmentContext();
  const confirmationDialog = useConfirmationDialog();
  const labels = getActionLabels(t);

  const { archive } = useLalSegmentsOperation();

  const [loadingAction, setLoadingAction] = useState<SegmentRowActionType | null>(null);

  const setLoading = useCallback(
    (action: SegmentRowActionType | null) => {
      setLoadingAction(action);
      confirmationDialog.setLoading(!!action);
    },
    [confirmationDialog]
  );

  const setConfirmationDisabled = useCallback(
    (disabled: boolean) => confirmationDialog.toogleConfirmationDisabled(disabled),
    [confirmationDialog]
  );

  const actions = useMemo(
    () => ({
      [lalSegmentRowActionType.DELETE]: archive,
    }),
    [archive]
  );

  const callLalSegmentAction = useCallback(
    <T extends LalSegmentBase>({
      action,
      content,
      segments,
      isBulkMode = false,
      skipModal = false,
      onExtraAction = NO_OP,
      onFinish,
      preProcessSegments = (segments): T[] => segments as unknown as T[],
    }: CallLalSegmentsAction<T>) => {
      const onConfirmed = async (segments: T[]) => {
        try {
          setLoading(action);
          const processedSegments = preProcessSegments(segments);
          await actions[action](processedSegments);
        } finally {
          setLoading(null);
          onFinish?.();
        }
      };

      if (skipModal) {
        return onConfirmed(segments);
      }

      const segmentLabel = isBulkMode ? t('LAL Segments') : t('LAL Segment');
      const confirmationButtonColor = 'error';
      const extraButtonOptions: ExtraButtonOptions | undefined =
        action === lalSegmentRowActionType.DELETE
          ? {
              extraBtnLabel: t('Refresh'),
              extraButtonColor: 'secondary',
              onExtraAction: onExtraAction,
              showExtraBtn: true,
            }
          : undefined;

      openActionModal({
        action,
        confirmationButtonColor,
        confirmationDialog,
        confirmationLabel: labels[action],
        content,
        extraButtonOptions,
        isBulkMode,
        onConfirmed: onConfirmed,
        segments,
        title: t('{action} {segment}', {
          action: labels[action],
          segment: segmentLabel,
        }),
      });
    },
    [t, setLoading, actions, confirmationDialog, labels]
  );

  return { callLalSegmentAction, loadingAction, setConfirmationDisabled };
};
